import dayjs from 'dayjs';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React from 'react';
import apiClient from '../../requests/api/apiClient';
import { fetchReadingsFromAPI } from '../../services/readingsService';
import { prepareData as prepareRootsData } from '../Highcharts/RootsChart/builder';
import calculateDailyMaxPlant from '../Highcharts/LineCharts/PlantsChart/utils';
import { buildCategoriesFromDates } from '../Highcharts/utils';

export interface FetchCtx {
  farmId: string;
  sectorId: string;
  startDate: string;
  endDate: string;
  wiseconnId: string;
  weatherZoneId: string;
  sn: string;
  telemetryType: string;
}

export interface ChartSerie {
  name: string;
  color: string;
  unit: string;
  type: 'line' | 'column';
  data: { x: number; y: number }[];
}

export interface PredefinedSerie {
  id: string;
  label: string;
  group: string;
  color: string;
  type: 'column' | 'line';
  requiresWeatherZone?: boolean;
  requiresDepthSelection?: boolean;
}

export interface CustomGrapherTabProps {
  farmId: string;
  sectorId: string;
  wiseconnId?: string;
  weatherZoneId?: string;
  sn?: string;
  telemetryType?: string;
}

export interface SavedGraph {
  id: string;
  name: string;
  checked: string[];
  selectedDepths: Record<string, string[]>;
  startDate: string | null;
  endDate: string | null;
}

export const DEPTH_PALETTE = [
  '#1565C0', '#AD1457', '#2E7D32', '#E65100', '#6A1B9A',
  '#00838F', '#F57F17', '#4E342E', '#37474F', '#558B2F',
];

export const SERIES: PredefinedSerie[] = [
  {
    id: 'riego', label: 'Riego (mm)', group: 'Riego', color: '#536e7b', type: 'column',
  },
  {
    id: 'lluvia', label: 'Lluvia (mm)', group: 'Clima', color: '#35a0d7', type: 'column', requiresWeatherZone: true,
  },
  {
    id: 'et0', label: 'ET0 (mm)', group: 'Clima', color: '#FEBF23', type: 'column', requiresWeatherZone: true,
  },
  {
    id: 'kc', label: 'Kc', group: 'Clima', color: '#8BC34A', type: 'column', requiresWeatherZone: true,
  },
  {
    id: 'dpv', label: 'DPV (kPa)', group: 'Clima', color: '#f29305', type: 'line', requiresWeatherZone: true,
  },
  {
    id: 'temperature', label: 'Temperatura (°C)', group: 'Clima', color: '#F44336', type: 'line', requiresWeatherZone: true,
  },
  {
    id: 'humidity', label: 'Humedad (%)', group: 'Clima', color: '#2196F3', type: 'line', requiresWeatherZone: true,
  },
  {
    id: 'solar', label: 'Radiación solar (W/m²)', group: 'Clima', color: '#764500', type: 'line', requiresWeatherZone: true,
  },
  {
    id: 'viento', label: 'Viento (km/h)', group: 'Clima', color: '#607D8B', type: 'line', requiresWeatherZone: true,
  },
  {
    id: 'max_temp', label: 'Max temperatura (°C)', group: 'Clima', color: '#D32F2F', type: 'line', requiresWeatherZone: true,
  },
  {
    id: 'min_temp', label: 'Min temperatura (°C)', group: 'Clima', color: '#1976D2', type: 'line', requiresWeatherZone: true,
  },
  {
    id: 'horas_frio_acum', label: 'Horas frío acumuladas', group: 'Clima', color: '#00ACC1', type: 'line', requiresWeatherZone: true,
  },
  {
    id: 'dias_grado_acum', label: 'Días grado acumulados', group: 'Clima', color: '#FF6F00', type: 'line', requiresWeatherZone: true,
  },
  {
    id: 'grados_dia', label: 'Grados día', group: 'Clima', color: '#E65100', type: 'column', requiresWeatherZone: true,
  },
  {
    id: 'horas_frio_dia', label: 'Horas frío día', group: 'Clima', color: '#0097A7', type: 'column', requiresWeatherZone: true,
  },
  {
    id: 'precip_acum', label: 'Precipitación acumulada (mm)', group: 'Clima', color: '#1565C0', type: 'line', requiresWeatherZone: true,
  },
  {
    id: 'et0_acum', label: 'ET0 acumulado (mm)', group: 'Clima', color: '#C6A700', type: 'line', requiresWeatherZone: true,
  },
  {
    id: 'suelo acum', label: 'Sumatoria humedad de suelo (%)', group: 'Suelo', color: '#d29e4b', type: 'line',
  },
  {
    id: 'suelo_prof', label: 'Humedad de suelo por profundidad (%)', group: 'Suelo', color: '#5D8AA8', type: 'line', requiresDepthSelection: true,
  },
  {
    id: 'salinidad', label: 'Salinidad (VIC)', group: 'Suelo', color: '#9C27B0', type: 'line', requiresDepthSelection: true,
  },
  {
    id: 'temp_suelo', label: 'Temperatura suelo (°C)', group: 'Suelo', color: '#FF5722', type: 'line', requiresDepthSelection: true,
  },
  {
    id: 'prof_riego', label: 'Profundidad de riego (cm)', group: 'Suelo', color: '#3F51B5', type: 'column',
  },
  {
    id: 'raices', label: 'Raíces (cm)', group: 'Suelo', color: '#8D6E63', type: 'column',
  },
  {
    id: 'planta', label: 'Planta (um)', group: 'Planta', color: '#4CAF50', type: 'line',
  },
  {
    id: 'max_planta', label: 'Max planta (um)', group: 'Planta', color: '#FF6600', type: 'line',
  },
  {
    id: 'ph', label: 'pH', group: 'Riego', color: '#E91E63', type: 'line',
  },
  {
    id: 'conductividad', label: 'Conductividad de suelo (mS/cm)', group: 'Riego', color: '#00BCD4', type: 'line',
  },
  {
    id: 'et0_entre_riegos', label: 'ET0 entre riegos (mm)', group: 'Riego', color: '#FFA000', type: 'column',
  },
];

export const assignDepthColors = (series: ChartSerie[]): ChartSerie[] => series.map((s, i) => ({
  ...s, color: s.color || DEPTH_PALETTE[i % DEPTH_PALETTE.length],
}));

export const toXY = (dateRef: string | Date | undefined, y: number) => ({
  x: dayjs(dateRef).valueOf(), y: Number(y),
});

export const toPoints = (readings: any[], xField: string, yField: string) => (readings || [])
  .map((r: any) => toXY(r[xField], r[yField]))
  .filter((p) => !Number.isNaN(p.y));

export const groupByDepth = (
  readings: any[],
  labelPrefix: string,
  serieType: 'line' | 'column',
): ChartSerie[] => {
  const groups: Record<string, any[]> = {};
  (readings || []).forEach((r: any) => {
    const key = r.sensorDepth != null ? `${r.sensorDepth}${r.depthUnit || 'cm'}` : 'default';
    if (!groups[key]) groups[key] = [];
    groups[key].push(r);
  });
  return Object.entries(groups).map(([depth, items]) => ({
    name: depth === 'default' ? labelPrefix : `${labelPrefix} ${depth}`,
    color: '',
    unit: items[0]?.unit || '',
    type: serieType,
    data: toPoints(items, 'dateTime', 'value'),
  }));
};

export async function fetchSerie(id: string, ctx: FetchCtx): Promise<ChartSerie[]> {
  const {
    farmId, sectorId, startDate, endDate,
    wiseconnId, weatherZoneId, sn, telemetryType,
  } = ctx;

  const isSn = telemetryType === 'sn';

  const weatherParams = {
    sectorId,
    wiseconnZoneId: weatherZoneId,
    sn: isSn ? weatherZoneId : undefined,
    telemetryType,
  };

  const sectorParams = {
    sectorId,
    wiseconnZoneId: wiseconnId,
    sn: isSn ? sn : undefined,
    telemetryType,
  };

  const base = { farmId, startDate, endDate };

  const serie = SERIES.find((s) => s.id === id);
  const color = serie?.color || '#2196F3';
  const type = serie?.type || 'line';

  switch (id) {
    case 'riego': {
      const { data } = await (apiClient as any).readings.getIrrigationChartData({
        ...base, ...sectorParams, weatherZoneId,
      });
      return [{
        name: 'Riego (mm)', color, unit: 'mm', type, data: toPoints(data.normalizedIrrigation || [], 'dateTime', 'cumulativeTotal'),
      }];
    }
    case 'lluvia': {
      const { data } = await (apiClient as any).readings.getIrrigationChartData({
        ...base, ...sectorParams, weatherZoneId,
      });
      return [{
        name: 'Lluvia (mm)', color, unit: 'mm', type, data: toPoints(data.rain || [], 'dateTime', 'cumulativeTotal'),
      }];
    }
    case 'et0': {
      const { data } = await (apiClient as any).readings.getIrrigationChartData({
        ...base, ...sectorParams, weatherZoneId,
      });
      return [{
        name: 'ET0 (mm)',
        color,
        unit: 'mm',
        type,
        data: (data.normalizedEt0 || []).map(
          (r: any) => toXY(r.date, r.et0),
        ).filter((p: any) => !Number.isNaN(p.y)),
      }];
    }
    case 'kc': {
      const { data } = await (apiClient as any).readings.getKcData({
        ...base, ...sectorParams, weatherZoneId,
      });
      return [{
        name: 'Kc',
        color,
        unit: '',
        type,
        data: (data.kcData || []).map(
          (r: any) => toXY(r.date, r.kc),
        ).filter((p: any) => !Number.isNaN(p.y)),
      }];
    }
    case 'dpv': {
      const { data } = await (apiClient as any).readings.getDpvData({ ...base, ...weatherParams });
      const dpvData = data.dpvSeries?.[0]?.data || [];
      return [{
        name: 'DPV (kPa)', color, unit: 'kPa', type, data: dpvData.map((p: any) => ({ x: p.x, y: p.y })),
      }];
    }
    case 'temperature': {
      const readings = await fetchReadingsFromAPI({
        ...base, granularity: 'all', sensorType: 'Temperature', ...weatherParams,
      });
      return [{
        name: 'Temperatura (°C)', color, unit: '°C', type, data: toPoints(readings || [], 'dateTime', 'value'),
      }];
    }
    case 'humidity': {
      const readings = await fetchReadingsFromAPI({
        ...base, granularity: 'all', sensorType: 'Humidity', ...weatherParams,
      });
      return [{
        name: 'Humedad (%)', color, unit: '%', type, data: toPoints(readings || [], 'dateTime', 'value'),
      }];
    }
    case 'solar': {
      const readings = await fetchReadingsFromAPI({
        ...base, granularity: 'all', sensorType: 'Solar Radiation', ...weatherParams,
      });
      return [{
        name: 'Radiación solar (W/m²)', color, unit: 'W/m²', type, data: toPoints(readings || [], 'dateTime', 'value'),
      }];
    }
    case 'viento': {
      const { data } = await (apiClient as any).readings.getWindVelocityData(
        { ...base, ...weatherParams },
      );
      const velocityData = (data.velocitySeries || [])[0]?.data || [];
      return [{
        name: 'Viento (km/h)', color, unit: 'km/h', type, data: velocityData.map((p: any) => ({ x: p.x, y: p.y })),
      }];
    }
    case 'suelo acum': {
      const { data } = await (apiClient as any).readings.getSoilMoistureData({
        ...base, sensorType: 'Soil Moisture', ...sectorParams,
      });
      const summed = data.summedSeries || [];
      return [{
        name: 'Sumatoria humedad de suelo', color, unit: '%', type, data: summed.map((p: any) => ({ x: p.x, y: p.y })).filter((p: any) => !Number.isNaN(p.y)),
      }];
    }
    case 'suelo_prof': {
      const { data } = await (apiClient as any).readings.getStackedSoilMoistureData({
        ...base, sensorType: 'Soil Moisture', ...sectorParams,
      });
      const { series: rawSeries = [] } = data.seriesData || {};
      const parsed = (rawSeries as { name: string;
        data: { x: number; y: number }[] }[]).map((s) => ({
        name: s.name,
        color: '',
        unit: '%',
        type: 'line' as const,
        data: (s.data || []).map((p: any) => (
          { x: p.x, y: p.y })).filter((p) => !Number.isNaN(p.y)),
      }));
      return assignDepthColors(parsed);
    }
    case 'salinidad': {
      const readings = await fetchReadingsFromAPI({
        ...base, granularity: 'all', sensorType: 'Salinity', ...sectorParams,
      });
      return assignDepthColors(groupByDepth(readings, 'Salinidad', type));
    }
    case 'temp_suelo': {
      const readings = await fetchReadingsFromAPI({
        ...base, granularity: 'all', sensorType: 'Soil Temperature', ...sectorParams,
      });
      return assignDepthColors(groupByDepth(readings, 'Temp. suelo', type));
    }
    case 'prof_riego': {
      const result = await prepareRootsData({
        ...base, sectorId, wiseconnZoneId: wiseconnId, sn, telemetryType,
      });
      return [{
        name: 'Profundidad de riego (cm)', color, unit: 'cm', type, data: result.irrigationSeries.series.map((p: any) => ({ x: p.x, y: p.y })),
      }];
    }
    case 'raices': {
      const result = await prepareRootsData(
        {
          ...base, sectorId, wiseconnZoneId: wiseconnId, sn, telemetryType,
        },
      );
      return [{
        name: 'Raíces (cm)', color, unit: 'cm', type, data: result.rootsSeries.series.map((p: any) => ({ x: p.x, y: p.y })),
      }];
    }
    case 'planta': {
      const readings = await fetchReadingsFromAPI({
        ...base, granularity: 'all', sensorType: 'Plant Caliper', ...sectorParams,
      });
      const groups: Record<string, any[]> = {};
      (readings || []).forEach((r: any) => {
        const key = r.name || r.sensorType || 'Planta';
        if (!groups[key]) groups[key] = [];
        groups[key].push(r);
      });
      return Object.entries(groups).map(([name, items]) => ({
        name, color, unit: items[0]?.unit || 'mm', type, data: toPoints(items, 'dateTime', 'value'),
      }));
    }
    case 'ph': {
      const readings = await fetchReadingsFromAPI({
        ...base, granularity: 'all', sensorType: 'pH', ...sectorParams,
      });
      return [{
        name: 'pH', color, unit: 'pH', type, data: toPoints(readings || [], 'dateTime', 'value'),
      }];
    }
    case 'conductividad': {
      const readings = await fetchReadingsFromAPI({
        ...base, granularity: 'all', sensorType: 'Irrigation Electrical Conductivity', ...sectorParams,
      });
      return [{
        name: 'Conductividad de suelo (mS/cm)', color, unit: 'mS/cm', type, data: toPoints(readings || [], 'dateTime', 'value'),
      }];
    }
    case 'max_temp': {
      const { data } = await (apiClient as any).readings
        .getHumidityTemperatureChartData({ ...base, ...weatherParams });
      const maxSerie = (data.series || [])
        .find((s: any) => s.name === 'Max Temperature');
      return [{
        name: 'Max temperatura (°C)',
        color,
        unit: '°C',
        type,
        data: toPoints(maxSerie?.data || [], 'dateTime', 'value'),
      }];
    }
    case 'min_temp': {
      const { data } = await (apiClient as any).readings
        .getHumidityTemperatureChartData({ ...base, ...weatherParams });
      const minSerie = (data.series || [])
        .find((s: any) => s.name === 'Min Temperature');
      return [{
        name: 'Min temperatura (°C)',
        color,
        unit: '°C',
        type,
        data: toPoints(minSerie?.data || [], 'dateTime', 'value'),
      }];
    }
    case 'horas_frio_acum': {
      const { data } = await (apiClient as any).readings
        .getColdHoursChartData({
          ...base, ...weatherParams,
        });
      const coldSerie = (data.series || [])
        .find((s: any) => s.name === 'Horas frío acumuladas');
      return [{
        name: 'Horas frío acumuladas',
        color,
        unit: 'hrs',
        type,
        data: (coldSerie?.data || []).map((p: any) => (
          { x: p.x, y: p.y }
        )).filter((p: any) => p.y != null && !Number.isNaN(p.y)),
      }];
    }
    case 'dias_grado_acum': {
      const { data } = await (apiClient as any).readings
        .getColdHoursChartData({
          ...base, ...weatherParams,
        });
      const degreeSerie = (data.series || [])
        .find((s: any) => s.name === 'Días grado acumulados');
      return [{
        name: 'Días grado acumulados',
        color,
        unit: 'GDA',
        type,
        data: (degreeSerie?.data || []).map((p: any) => (
          { x: p.x, y: p.y }
        )).filter((p: any) => p.y != null && !Number.isNaN(p.y)),
      }];
    }
    case 'grados_dia': {
      const { data } = await (apiClient as any).readings
        .getDegreeDayChartData({
          ...base, ...weatherParams,
        });
      return [{
        name: 'Grados día',
        color,
        unit: 'GD',
        type,
        data: (data.degreeDay || []).map((p: any) => (
          { x: p.x, y: p.y }
        )).filter((p: any) => !Number.isNaN(p.y)),
      }];
    }
    case 'horas_frio_dia': {
      const { data } = await (apiClient as any).readings
        .getDegreeDayChartData({
          ...base, ...weatherParams,
        });
      return [{
        name: 'Horas frío día',
        color,
        unit: 'hrs',
        type,
        data: (data.coldHours || []).map((p: any) => (
          { x: p.x, y: p.y }
        )).filter((p: any) => !Number.isNaN(p.y)),
      }];
    }
    case 'max_planta': {
      const { data } = await (apiClient as any).readings
        .getPlantData({ ...base, ...sectorParams });
      const maxSeries = calculateDailyMaxPlant(data.series || []);
      return maxSeries.map((s: any) => ({
        name: s.name,
        color: s.color || color,
        unit: 'um',
        type,
        data: (s.data || []).map((p: any) => (
          { x: p.x, y: p.y }
        )).filter((p: any) => !Number.isNaN(p.y)),
      }));
    }
    case 'precip_acum': {
      const readings = await fetchReadingsFromAPI({
        ...base, granularity: 'daily', sensorType: 'Rain', ...weatherParams,
      });
      const days = buildCategoriesFromDates(startDate, endDate);
      const rainMap: Record<string, number> = {};
      (readings || []).forEach((r: any) => {
        rainMap[r.date] = r.cumulativeTotal;
      });
      let cumSum = 0;
      const pts = days.map((day) => {
        const val = rainMap[day];
        if (val != null) cumSum += val;
        return { x: dayjs(day).valueOf(), y: cumSum };
      });
      return [{
        name: 'Precipitación acumulada (mm)',
        color,
        unit: 'mm',
        type,
        data: pts,
      }];
    }
    case 'et0_acum': {
      const { data } = await (apiClient as any).readings
        .getET0Data({
          ...base, ...weatherParams,
        });
      const days = buildCategoriesFromDates(startDate, endDate);
      const pts = days.map((day) => {
        const d = data.et0?.[day];
        if (!d) return null;
        return { x: dayjs(day).valueOf(), y: d.cumulativeET0 };
      }).filter(Boolean);
      return [{
        name: 'ET0 acumulado (mm)',
        color,
        unit: 'mm',
        type,
        data: pts as { x: number; y: number }[],
      }];
    }
    case 'et0_entre_riegos': {
      const { data } = await (apiClient as any).readings
        .getKcData({
          ...base, ...sectorParams, weatherZoneId,
        });
      const et0ForKc = data.accumulatedEt0ForKc || [];
      return [{
        name: 'ET0 entre riegos (mm)',
        color,
        unit: 'mm',
        type,
        data: et0ForKc.map((p: any) => (
          { x: dayjs(p.date).valueOf(), y: p.et0 }
        )).filter((p: any) => !Number.isNaN(p.y)),
      }];
    }
    default:
      return [];
  }
}

interface ChartRendererProps {
  highcharts: typeof Highcharts;
  options: Highcharts.Options;
  chartKey: string;
  onError: () => void;
}

interface ChartRendererState { crashed: boolean }

export class ChartRenderer extends React.Component<ChartRendererProps, ChartRendererState> {
  constructor(props: ChartRendererProps) {
    super(props);
    this.state = { crashed: false };
  }

  componentDidCatch() {
    this.setState({ crashed: true });
    this.props.onError();
  }

  static getDerivedStateFromError() {
    return { crashed: true };
  }

  render() {
    if (this.state.crashed) return null;
    return (
      <HighchartsReact
        highcharts={this.props.highcharts}
        options={this.props.options}
        key={this.props.chartKey}
      />
    );
  }
}
